import { ChangeDetectionStrategy, Component, Input, computed, effect, signal } from '@angular/core';
import { Store } from '@ngneat/elf';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { DlsOverrideModel } from 'src/app/shared/models/dlsOverride.model';
import { TrajectoryModel } from 'src/app/shared/models/trajectory.model';
import { buildTrajectory2dPlot } from '../shared/helpers/trajectory-2d-plot.helper';

@Component({
    selector: 'app-two-dee-plot',
    template: `
    <app-xy-line-plot
      [plotData]="plotData()"
      [xAxisTitle]="xAxisTitle()"
      [yAxisTitle]="yAxisTitle()"
      [plotName]="plotName()"
      [downloadPlotName]="downloadPlotName()"
      [plotTitle]="'Trajectory Plot'"
      [reverseAutoRange]="reverseAutoRange()"
      btmMargin="45"
      [xyLinePlotStore]="xyLinePlotStore()"
    ></app-xy-line-plot>
  `,
    styles: [':host { display: flex; flex: 1 1 0%; min-height: 0; overflow: hidden; }'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TwoDeePlotComponent {
  private _trajectoryData = signal<TrajectoryModel[]>([]);
  private _selectedPlot = signal('northingTvd');
  private _userUnits = signal<UserUnitsModel | null>(null);
  private _dlsOverrides = signal<DlsOverrideModel[] | undefined>(undefined);

  @Input() set trajectoryData(value: TrajectoryModel[] | null) {
    this._trajectoryData.set(value ?? []);
  }

  @Input() set selectedPlot(value: string | null) {
    this._selectedPlot.set(value ?? 'northingTvd');
  }

  @Input() set userUnits(value: UserUnitsModel | null) {
    this._userUnits.set(value);
  }

  @Input() set trajectoryPlotStore(value: Store | null) {
    this.xyLinePlotStore.set(value);
  }

  @Input() set dlsOverrides(value: DlsOverrideModel[] | undefined) {
    this._dlsOverrides.set(value);
  }

  public xAxisTitle = signal('');
  public yAxisTitle = signal('');
  public plotName = computed(() => `Trajectory Plot ${this._selectedPlot()}`);
  public downloadPlotName = signal('');
  public reverseAutoRange = signal(false);
  public plotData = signal<unknown[]>([]);
  public xyLinePlotStore = signal<Store | null>(null);

  constructor() {
    effect(() => {
      this._setPlotData(this._trajectoryData());
    });
  }

  private _setPlotData(trajectoryData: TrajectoryModel[]) {
    const plotResult = buildTrajectory2dPlot({
      trajectoryData,
      selectedPlot: this._selectedPlot(),
      userUnits: this._userUnits(),
      dlsOverrides: this._dlsOverrides()
    });

    if (!plotResult) {
      return;
    }
    this.plotData.set(plotResult.plotData);
    this.yAxisTitle.set(plotResult.yAxisTitle);
    this.xAxisTitle.set(plotResult.xAxisTitle);
    this.reverseAutoRange.set(plotResult.reverseAutoRange);
    this.downloadPlotName.set(plotResult.downloadPlotName);
  }
}
